import React, { useState, useEffect, useRef } from "react";
import { base64FileDownload } from "../../utils/FileDownloadUtil";
import HasPermissionTo from "../../common/HasPermissionTo";
import { withTranslation } from "react-i18next";
import NscBusinessSettings from "./NscBusinessSettings";
import DealerBusinessSettings from "./DealerBusinessSettings";
import TermsAndConditionsClient from "../../shared/clients/TermsAndConditionsClient";
import PendingActionClient from "../../shared/clients/PendingActionClient";
import { TERMS_AND_CONDITIONS } from "../../common/Constants";

const BusinessSettings = (props) => {
  const [isDealerAcceptingTerms, setIsDealerAcceptingTerms] = useState(false);
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [termsAndConditionsHistory, setTermsAndConditionsHistory] = useState(
    []
  );
  const [enableUpload, setEnableUpload] = useState(undefined);
  const [restrictDropdown, setRestrictDropdown] = useState(false);

  const selectedTab = useRef(0);
  const filteredDealers = useRef([]);
  const fromPending = useRef(false);

  const pendingAction =
    props.location.query && props.location.query.fromPendingActions;

  useEffect(() => {
    if (
      pendingAction &&
      props.location.actionName === TERMS_AND_CONDITIONS &&
      props.user.businessSettingsPage === -1
    ) {
      props.user.updateBusinessSettingsTab(0);
      fromPending.current = true;
    }
    filteredDealers.current = filterDealers();
    setIsDataLoaded(true);
  }, []);

  const handleTermsAndConditionsDownload = (termsandconditions) => {
    base64FileDownload(
      termsandconditions.file,
      "application/pdf",
      `${
        props.user.market.toUpperCase() +
        " " +
        props.t("TermsandConditionsHistory.documentName") +
        termsandconditions.version
      }.pdf`
    );
  };

  const resetFromPending = () => {
    fromPending.current = false;
    setIsDealerAcceptingTerms(false);
    setRestrictDropdown(false);
  };

  const alterComponentView = (termsAndConditions) => {
    const commonId = props.user.commonId;

    setIsDealerAcceptingTerms((prev) => !prev);
    setAcceptTermsAndConditions(termsAndConditions);
    setRestrictDropdown((prev) => !prev);

    setTimeout(() => {
      const acceptedDealers = termsAndConditions.acceptedDealers || [];
      let isCurrentDealerAcceptingTerms = false;

      acceptedDealers.forEach(function (dealer) {
        if (dealer === commonId) {
          isCurrentDealerAcceptingTerms = true;
        }
      });

      const pendingActionsLength = props.user.pendingActions?.length || 0;

      if (pendingActionsLength === 0 && isCurrentDealerAcceptingTerms) {
        props.updateDealerGroupDetails();
      }

      if (fromPending.current) {
        props.history.push("/dashboard");
      } else {
        loadTermsAndConditionsHistory();
      }
    }, 0);
  };

  const deleteTerms = (version) => {
    const termsList = [...termsAndConditionsHistory];
    props.hideOrShow(true);

    TermsAndConditionsClient.deleteTermsAndConditions(props.user, version)
      .then(() => {
        addTermsAndConditionsPA(version);
        const updatedTermsList = termsList.filter(
          (terms) => terms.version !== version
        );
        setTermsAndConditionsHistory(updatedTermsList);
        setEnableUpload(!(updatedTermsList.length > 1));
      })
      .finally(() => {
        props.hideOrShow(false);
      });
  };

  const addTermsAndConditionsPA = () => {
    props.hideOrShow(true);
    PendingActionClient.addPA(props.user, TERMS_AND_CONDITIONS).finally(() => {
      props.hideOrShow(false);
    });
  };

  const loadTermsAndConditionsHistory = () => {
    props.hideOrShow(true);

    TermsAndConditionsClient.retrieveTermsAndConditionsHistory(props.user)
      .then((data) => {
        setTermsAndConditionsHistory(data);
        setEnableUpload(!(data.length > 1));

        // If this is coming from the dashboard
        if (
          pendingAction &&
          props.location.actionName === "TERMS_CONDITIONS" &&
          !props.isNsc &&
          props.user.businessSettingsPage === 0 &&
          fromPending.current
        ) {
          const tnC = data
            .slice()
            .reverse()
            .find((terms) => !terms.accepted);

          if (tnC) {
            setIsDealerAcceptingTerms(true);
            setRestrictDropdown(true);
            setAcceptTermsAndConditions(tnC);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsDataLoaded(true);
        props.hideOrShow(false);
      });
  };

  const filterDealers = () => {
    const filtered = [];

    if (props.dealerGroup) {
      const currentDealer = props.dealerGroup.find(
        (dealer) => dealer.commonId === props.user.commonId
      );

      if (
        currentDealer &&
        props.user.showAll &&
        (currentDealer.dealerType === "HUB" ||
          currentDealer.dealerType === "MDLR")
      ) {
        props.dealerGroup.forEach((dealer) => {
          filtered.push(dealer);
        });
      } else if (currentDealer) {
        filtered.push(currentDealer);
      }
    }

    return filtered;
  };

  if (isDataLoaded) {
    return (
      <div>
        <HasPermissionTo
          perform={["dealerBusinessSettings:view"]}
          permissions={props.user.permissions.rolePermissions}
          render={() => (
            <DealerBusinessSettings
              {...props}
              startingTab={selectedTab.current}
              restrictDropdown={restrictDropdown}
              isDealerAcceptingTerms={isDealerAcceptingTerms}
              fromPending={fromPending.current}
              resetFromPending={resetFromPending}
              termsAndConditions={acceptTermsAndConditions}
              alterComponentView={alterComponentView}
              loadTermsAndConditionsHistory={loadTermsAndConditionsHistory}
              handleTermsAndConditionsDownload={
                handleTermsAndConditionsDownload
              }
              termsAndConditionsHistory={termsAndConditionsHistory}
              filteredDealers={filteredDealers.current}
              isDealer={true}
            />
          )}
          noRender={() => (
            <NscBusinessSettings
              {...props}
              startingTab={selectedTab.current}
              restrictDropdown={restrictDropdown}
              handleTermsAndConditionsDownload={
                handleTermsAndConditionsDownload
              }
              isDealer={false}
              enableUpload={enableUpload}
              deleteTerms={deleteTerms}
              loadTermsAndConditionsHistory={loadTermsAndConditionsHistory}
              termsAndConditionsHistory={termsAndConditionsHistory}
            />
          )}
        />
      </div>
    );
  }

  return null;
};

export default withTranslation("emp")(BusinessSettings);
